import {
    finiteRegionNumber,
    regionColor,
    regionUniformShader,
    createRegionUniforms,
    writeRegionUniform,
    type RegionUniform,
} from './region-shape';
import { applyAreaShader, areaBounds, combineAreaShader, drawableAreas, type AreaShape, type ResolvedArea } from './area-shape';
import { outlineSlabShader } from './outline-shape';
import { splatObjectLaneCall } from './splat-objects';
import type { RegionColorEffect } from './splat-region-effects';
import type { SceneTrigger } from '../types';

/**
 * A focus region: a sphere, box, outline or whole splat objects (the shared shape picker, see
 * area-shape.ts) where the scene stays prominent, or with `apply: 'outside'` everywhere except there.
 */
export interface FocusRegion extends AreaShape {
    id: string;
    name: string;
    enabled: boolean;
    /**
     * This region's highlight colour, #rrggbb. Unset: the config's `tint`, which is what every
     * region of a scene saved before per-region colours uses.
     */
    tint?: string;
    /**
     * Optional runtime switches (same kinds as overlay triggers). Without triggers the region
     * is always on; with them it applies only while a trigger holds or after an event fires,
     * and a hotspot click toggles it. The editor honours them too, except for the region being edited.
     */
    triggers?: SceneTrigger[];
}
export interface FocusConfig {
    enabled: boolean;
    regions: FocusRegion[];
    showBounds?: boolean;
    /** Highlight colour of regions without their own `tint`. Default #ffd166. */
    tint?: string;
    tintStrength?: number;
    outsideBrightness?: number;
    outsideSaturation?: number;
}
export const MAX_FOCUS_REGIONS = 4;
export const DEFAULT_FOCUS_TINT = '#ffd166';
export interface ResolvedFocusRegion {
    id: string;
    name: string;
    enabled: true;
    area: ResolvedArea;
    /** The region's own colour; absent when it uses the config's. */
    tint?: string;
    triggers?: SceneTrigger[];
}
/** The regions the effect draws, in uniform slot order (enabled, drawable, at most MAX_FOCUS_REGIONS). */
export function drawableFocusRegions<T extends { enabled?: boolean }>(regions: ReadonlyArray<T | null | undefined> | undefined) {
    return drawableAreas<T>(regions, MAX_FOCUS_REGIONS);
}
export function resolveFocusConfig(config: FocusConfig | undefined) {
    if (!config?.enabled) return undefined;
    const regions: ResolvedFocusRegion[] = drawableFocusRegions<FocusRegion>(config.regions).map(({ item: r, area }) => ({
        id: r.id,
        name: r.name,
        enabled: true,
        area,
        ...(regionColor(r.tint, '') ? { tint: regionColor(r.tint, '') } : {}),
        ...(Array.isArray(r.triggers) ? { triggers: r.triggers } : {}),
    }));
    if (!regions.length) return undefined;
    return {
        regions,
        showBounds: config.showBounds !== false,
        tint: regionColor(config.tint, DEFAULT_FOCUS_TINT),
        tintStrength: finiteRegionNumber(config.tintStrength, 0.2, 0, 1),
        outsideBrightness: finiteRegionNumber(config.outsideBrightness, 0.35, 0, 1),
        outsideSaturation: finiteRegionNumber(config.outsideSaturation, 0.15, 0, 1),
    };
}
/**
 * The look rides in uniforms so it can change (slider edits, keyframe animation) without
 * recompiling: `uFocusTint` a = tint strength (rgb = the config's colour, kept for the API);
 * `uFocusOutside` x = surrounding brightness, y = surrounding saturation; and each region's
 * params `uFocusRegion<i>P` yzw = that region's highlight colour (its own, or the config's).
 * Only the region set, shape kinds and outlines live in the shader source.
 */
export const FOCUS_TINT_UNIFORM = 'uFocusTint';
export const FOCUS_OUTSIDE_UNIFORM = 'uFocusOutside';
export interface FocusLook {
    tint: { r: number; g: number; b: number };
    tintStrength: number;
    outsideBrightness: number;
    outsideSaturation: number;
}
export interface FocusUniforms {
    tint: Float32Array;
    outside: Float32Array;
    /** One per region slot (MAX_FOCUS_REGIONS): transform, feather (x) and highlight colour (yzw). */
    regions: RegionUniform[];
}
export const focusRegionUniform = (index: number) => `uFocusRegion${index}`;
export const createFocusUniforms = (): FocusUniforms => ({ tint: new Float32Array(4), outside: new Float32Array(4), regions: createRegionUniforms(MAX_FOCUS_REGIONS) });
const hexChannels = (hex: string) => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255);
/** The configured look (defaults applied), or the defaults when focus is off. */
export function resolveFocusLook(config: FocusConfig | undefined): FocusLook {
    const [r, g, b] = hexChannels(regionColor(config?.tint, DEFAULT_FOCUS_TINT));
    return {
        tint: { r, g, b },
        tintStrength: finiteRegionNumber(config?.tintStrength, 0.2, 0, 1),
        outsideBrightness: finiteRegionNumber(config?.outsideBrightness, 0.35, 0, 1),
        outsideSaturation: finiteRegionNumber(config?.outsideSaturation, 0.15, 0, 1),
    };
}
export function writeFocusUniforms(uniforms: FocusUniforms, look: FocusLook): void {
    uniforms.tint[0] = look.tint.r;
    uniforms.tint[1] = look.tint.g;
    uniforms.tint[2] = look.tint.b;
    uniforms.tint[3] = look.tintStrength;
    uniforms.outside[0] = look.outsideBrightness;
    uniforms.outside[1] = look.outsideSaturation;
}
/** A region's highlight colour: its own `tint`, else the config's (`fallback`, already resolved). */
export function focusRegionTint(region: { tint?: string } | undefined, fallback: FocusLook['tint']): FocusLook['tint'] {
    const own = regionColor(region?.tint, '');
    if (!own) return fallback;
    const [r, g, b] = hexChannels(own);
    return { r, g, b };
}
/** Write a region slot's highlight colour (params yzw). */
export function writeFocusRegionTint(target: RegionUniform, tint: FocusLook['tint']): void {
    target.params[1] = tint.r;
    target.params[2] = tint.g;
    target.params[3] = tint.b;
}
/**
 * @param uniforms Arrays the caller keeps to drive the look at runtime; created when omitted.
 *   They are (re)filled with the configured look.
 */
export function buildFocusEffect(config: FocusConfig | undefined, uniforms?: FocusUniforms): RegionColorEffect | undefined {
    const cfg = resolveFocusConfig(config);
    if (!cfg) return undefined;
    const target = uniforms ?? createFocusUniforms();
    const look = resolveFocusLook(config);
    writeFocusUniforms(target, look);
    cfg.regions.forEach((r, i) => {
        if (r.area.kind === 'sphere' || r.area.kind === 'box') writeRegionUniform(target.regions[i], r.area.region);
        writeFocusRegionTint(target.regions[i], focusRegionTint(r, look.tint));
    });
    const applies = cfg.regions.map((r) => r.area.apply);
    // Splat object regions: lane k is the k-th of them (splat-objects.ts).
    const objectSets: string[][] = [];
    const lanes = cfg.regions.map((r) => (r.area.kind === 'splats' ? objectSets.push(r.area.ids) - 1 : -1));
    const shader = (lang: 'glsl' | 'wgsl') => {
        const glsl = lang === 'glsl';
        const u = (name: string) => (glsl ? name : `uniform.${name}`);
        // Sphere / box: transform + feather from uniforms. Outline: baked. Splat objects: the
        // splat's object flags. Outline and object regions' params uniform still carries the colour.
        const paramsDecl = (i: number) => (glsl ? `uniform vec4 ${focusRegionUniform(i)}P;` : `uniform ${focusRegionUniform(i)}P: vec4f;`);
        const weights = cfg.regions
            .map((r, i) =>
                r.area.kind === 'outline'
                    ? `${paramsDecl(i)}\n${outlineSlabShader(r.area.slab, 'focusWeight' + i, lang)}\n`
                    : r.area.kind === 'splats'
                      ? `${paramsDecl(i)}\n${glsl ? `float focusWeight${i}(vec3 center) { return ${splatObjectLaneCall('focus', lanes[i])}; }` : `fn focusWeight${i}(center: vec3f) -> f32 { return ${splatObjectLaneCall('focus', lanes[i])}; }`}\n`
                      : regionUniformShader(r.area.kind, 'focusWeight' + i, focusRegionUniform(i), lang),
            )
            .join('\n');
        const decl = glsl ? 'float' : 'let';
        const vdecl = glsl ? 'vec3' : 'let';
        const terms = cfg.regions.map((_, i) => `a${i}`);
        const perRegion = cfg.regions.map((_, i) => `${decl} a${i} = ${applyAreaShader(`focusWeight${i}(center)`, applies[i])};`).join(' ');
        // Highlight colour: the regions' colours weighted by how much each applies, written as
        // c0 + sum((ci - c0) * ai / total) so that equal colours (every region of a scene saved
        // before per-region colours) give c0 exactly, the colour the effect has always used.
        const colour = (i: number) => `${u(focusRegionUniform(i))}P.yzw`;
        const tint =
            cfg.regions.length === 1
                ? `${vdecl} tint = ${colour(0)};`
                : `${decl} total = max(${terms.join(' + ')}, 0.00001); ${vdecl} tint = ${[
                      colour(0),
                      ...cfg.regions.slice(1).map((_, k) => `(${colour(k + 1)} - ${colour(0)}) * (a${k + 1} / total)`),
                  ].join(' + ')};`;
        const rgb = glsl ? 'color.rgb' : '(*color).rgb';
        const v3 = glsl ? 'vec3' : 'vec3f';
        const tintU = u(FOCUS_TINT_UNIFORM);
        const outside = u(FOCUS_OUTSIDE_UNIFORM);
        const statement = `${perRegion} ${decl} inside = ${combineAreaShader(terms, applies)}; ${tint}
            ${vdecl} original = ${rgb};
            ${vdecl} muted = mix(${v3}(dot(original, ${v3}(0.2126, 0.7152, 0.0722))), original, ${outside}.y) * ${outside}.x;
            ${vdecl} highlighted = mix(original, tint, ${tintU}.a);
            ${glsl ? 'color.rgb = mix(muted, highlighted, inside);' : '*color = vec4f(mix(muted, highlighted, inside), (*color).a);'}`;
        return glsl
            ? `uniform vec4 ${FOCUS_TINT_UNIFORM};
uniform vec4 ${FOCUS_OUTSIDE_UNIFORM};
${weights}
void region_focus(vec3 center, inout vec4 color) { ${statement} }`
            : `uniform ${FOCUS_TINT_UNIFORM}: vec4f;
uniform ${FOCUS_OUTSIDE_UNIFORM}: vec4f;
${weights}
fn region_focus(center: vec3f, color: ptr<function, vec4f>) { ${statement} }`;
    };
    return {
        key: 'focus',
        glsl: shader('glsl'),
        wgsl: shader('wgsl'),
        bounds: cfg.showBounds ? cfg.regions.map((r) => areaBounds(r.area)) : [],
        uniforms: {
            [FOCUS_TINT_UNIFORM]: target.tint,
            [FOCUS_OUTSIDE_UNIFORM]: target.outside,
            ...Object.fromEntries(cfg.regions.flatMap((r, i) => [
                ...(r.area.kind === 'sphere' || r.area.kind === 'box' ? [[focusRegionUniform(i), target.regions[i].matrix]] : []),
                [`${focusRegionUniform(i)}P`, target.regions[i].params],
            ])),
        },
        ...(objectSets.length ? { objectSets } : {}),
    };
}
